import { Dialog } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { bulkUpdateGrantTags, GrantTag, updateGrantTags } from "../../api/Esop";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import { Label } from "../../components/shared/InputField";
import { Grant } from "../../types/Grant";

type Props = {
  grants: Grant[];
  profiles: (GrantTag & { active: boolean })[];
  onClose: () => void;
  onSaved: () => void;
};

const toTags = (value: unknown) => (value || []) as GrantTag[];

export default function ManageGrantTagsDialog({
  grants,
  profiles,
  onClose,
  onSaved,
}: Props) {
  const isSingleGrant = grants.length === 1;
  const [singleTags, setSingleTags] = useState<GrantTag[]>([]);
  const [addTags, setAddTags] = useState<GrantTag[]>([]);
  const [removeTags, setRemoveTags] = useState<GrantTag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const activeOptions = useMemo(
    () =>
      profiles
        .filter((profile) => profile.active)
        .map(({ label, value }) => ({ label, value })),
    [profiles]
  );
  const removeOptions = useMemo(() => {
    const tags = new Map<string, { label: string; count: number }>();
    grants.forEach((grant) =>
      toTags(grant.tags).forEach((tag) => {
        const current = tags.get(tag.value) || { label: tag.label, count: 0 };
        tags.set(tag.value, { ...current, count: current.count + 1 });
      })
    );
    return [...tags.entries()].map(([value, tag]) => ({
      value,
      label: `${tag.label} · on ${tag.count} of ${grants.length} grants`,
    }));
  }, [grants]);

  useEffect(() => {
    setSingleTags(toTags(grants[0]?.tags));
    setAddTags([]);
    setRemoveTags([]);
  }, [grants]);

  const save = async () => {
    try {
      setIsSaving(true);
      if (isSingleGrant) {
        await updateGrantTags(grants[0].optionHolderId, singleTags);
      } else {
        await bulkUpdateGrantTags(
          grants.map((grant) => grant.optionHolderId),
          addTags,
          removeTags.map((tag) => tag.value)
        );
      }
      toast("Grant tags updated", { type: "success" });
      onSaved();
      onClose();
    } catch (error: any) {
      toast(error?.response?.data?.errorRaw || "Unable to update grant tags", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasBulkChanges = addTags.length > 0 || removeTags.length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <VStack className="gap-5 bg-white p-6">
        <Box>
          <Box className="text-lg font-semibold text-gray-800">
            Manage grant tags
          </Box>
          <Box className="mt-1 text-sm text-gray-500">
            {isSingleGrant
              ? "Update the tags for this grant."
              : `Update tags for ${grants.length} selected grants.`}
          </Box>
        </Box>
        {isSingleGrant ? (
          <Box>
            <Label className="text-sm font-normal">Tags</Label>
            <CreatableSelect
              isMulti
              value={singleTags}
              options={activeOptions}
              isValidNewOption={() => false}
              placeholder="Select tags"
              onChange={(value) => setSingleTags(toTags(value))}
            />
          </Box>
        ) : (
          <VStack className="gap-5">
            <Box>
              <Label className="text-sm font-normal">Add tags</Label>
              <CreatableSelect
                isMulti
                value={addTags}
                options={activeOptions.filter(
                  (tag) =>
                    !removeTags.some((removed) => removed.value === tag.value)
                )}
                isValidNewOption={() => false}
                placeholder="Select active tag profiles"
                onChange={(value) => {
                  const next = toTags(value);
                  setAddTags(next);
                  setRemoveTags((current) =>
                    current.filter(
                      (tag) => !next.some((added) => added.value === tag.value)
                    )
                  );
                }}
              />
            </Box>
            <Box>
              <Label className="text-sm font-normal">Remove tags</Label>
              <CreatableSelect
                isMulti
                value={removeTags}
                options={removeOptions.filter(
                  (tag) => !addTags.some((added) => added.value === tag.value)
                )}
                isValidNewOption={() => false}
                placeholder="Select tags to remove"
                onChange={(value) => {
                  const next = toTags(value);
                  setRemoveTags(next);
                  setAddTags((current) =>
                    current.filter(
                      (tag) =>
                        !next.some((removed) => removed.value === tag.value)
                    )
                  );
                }}
              />
            </Box>
            {hasBulkChanges && (
              <Box className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                {addTags.length > 0 &&
                  `Add ${addTags.map((tag) => tag.label).join(", ")}. `}
                {removeTags.length > 0 &&
                  `Remove ${removeTags
                    .map((tag) => tag.label.split(" · ")[0])
                    .join(", ")}.`}
              </Box>
            )}
          </VStack>
        )}
        <HStack className="justify-end gap-3 border-t border-gray-200 pt-4">
          <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          <ButtonPrimary
            loading={isSaving}
            disabled={!isSingleGrant && !hasBulkChanges}
            onClick={save}
          >
            Save tags
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Dialog>
  );
}
